import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAdBannerQuery } from "@/components/Banner/AdBanner";
import { PreviewDialogProvider } from "@/components/Search/PreviewDialogProvider";
import { MobileCategoryDrawer, MobileFilterSheet, ProductFilter, ProductList } from "@/components/edition/category";
import { BreadCrumb } from "@/components/edition/category/components/header/BreadCrumb";
import { CategoryBanner } from "@/components/edition/category/components/header/CategoryBanner";
import CategoryLNB from "@/components/edition/category/components/lnb/CategoryLNB";
import { CategoryMenuGrid } from "@/components/edition/category/components/midNav/CategoryMenuGrid";
import { useCategoryUrlState, useMobileUI } from "@/components/edition/category/hooks";
import CategoryPageSkeleton from "@/components/system/category/components/CategoryPageSkeleton";
import { CATEGORY_QUERY_KEYS, useCategoryProducts, useSystemCategoryData } from "@/components/system/category/hooks";
import { useScrollVisibility } from "@/hooks/useScrollVisibility";
import { cn } from "@/lib/utils";
import { Typography } from "@bookips/sds";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
const ITEMS_PER_PAGE = 24;
const CategoryPage = ()=>{
    const router = useRouter();
    const queryClient = useQueryClient();
    // GNBSubNav 표시 여부에 따라 LNB top 위치 조정
    const isSubNavVisible = useScrollVisibility({
        mode: "direction"
    });
    // URL 상태 관리 (필터, 정렬, 페이지)
    const { selectedCategoryId , currentPage , selectedSort , selectedFilters , handleSortChange , handleFilterChange , handleFilterReset , handlePageChange , resetFiltersAndSort  } = useCategoryUrlState();
    // 모바일 UI 상태
    const { isMobileFilterOpen , mobileFilterExpandedKey , isMobileCategoryDrawerOpen , openMobileFilter , closeMobileFilter , closeMobileCategoryDrawer  } = useMobileUI();
    // 카테고리 데이터 (API + 파생값)
    const { isMenuLoading , selectedSubject , isSubjectPage , lnbItems , lnbTitle , categoryPath , isLastDepth , directChildCategories , currentTitle , breadcrumbItems , depthCategories , getCategoryHref  } = useSystemCategoryData(selectedCategoryId);
    // 상품 데이터
    const productsQuery = useCategoryProducts({
        categoryId: selectedCategoryId,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        sort: selectedSort,
        filters: {
            units: selectedFilters.units,
            dataTypes: selectedFilters.dataTypes,
            authors: selectedFilters.authors,
            solvookPasses: selectedFilters.solvookPasses,
            steps: selectedFilters.steps
        }
    });
    // 배너 데이터
    const { adBannerResponse  } = useAdBannerQuery({
        position: "systemCategory"
    });
    const banners = useMemo(()=>{
        return (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items.map((item)=>({
                id: item.id,
                imageUrl: item.imageUrl,
                imageUrlPc: item.imageUrlPc,
                imageUrlTablet: item.imageUrlTablet,
                imageUrlMobile: item.imageUrlMobile,
                link: item.link
            }))) || [];
    }, [
        adBannerResponse
    ]);
    // 네비게이션 핸들러
    const handleCategorySelect = useCallback((categoryId)=>{
        if (categoryId === selectedCategoryId) {
            queryClient.invalidateQueries({
                queryKey: [
                    CATEGORY_QUERY_KEYS.CATEGORY_PRODUCTS
                ]
            });
            return;
        }
        router.push("/categories?id=".concat(categoryId));
    }, [
        selectedCategoryId,
        queryClient,
        router
    ]);
    const handleDepthCategoryChange = useCallback((id)=>{
        resetFiltersAndSort();
        queryClient.invalidateQueries({
            queryKey: [
                CATEGORY_QUERY_KEYS.CATEGORY_PRODUCTS
            ]
        });
        router.push("/categories?id=".concat(id));
    }, [
        resetFiltersAndSort,
        queryClient,
        router
    ]);
    if (isMenuLoading) {
        return /*#__PURE__*/ _jsx(CategoryPageSkeleton, {});
    }
    return /*#__PURE__*/ _jsx(PreviewDialogProvider, {
        children: /*#__PURE__*/ _jsx("div", {
            className: "min-h-screen bg-white",
            children: /*#__PURE__*/ _jsx("div", {
                className: "mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-0",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex gap-12",
                    children: [
                        /*#__PURE__*/ _jsx("aside", {
                            className: "hidden w-50 shrink-0 md:block lg:w-60",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: cn("sticky transition-all duration-200", isSubNavVisible ? "top-[120px]" : "top-[72px]"),
                                children: /*#__PURE__*/ _jsx(CategoryLNB, {
                                    title: lnbTitle,
                                    titleHref: "/categories?id=".concat(selectedSubject),
                                    items: lnbItems,
                                    selectedCategoryId: isSubjectPage ? undefined : selectedCategoryId,
                                    onCategorySelect: handleCategorySelect,
                                    depthCategories: depthCategories,
                                    currentDepthCategoryId: String(selectedSubject),
                                    onDepthCategoryChange: handleDepthCategoryChange,
                                    isLoading: isMenuLoading,
                                    getCategoryHref: getCategoryHref,
                                    pageType: "system"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs("main", {
                            className: "min-w-0 flex-1 pt-5 sm:pt-6",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-6 sm:gap-8",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-col gap-3 md:gap-4",
                                            children: [
                                                !isSubjectPage && categoryPath.length > 1 && /*#__PURE__*/ _jsx(BreadCrumb, {
                                                    items: breadcrumbItems,
                                                    pageType: "system"
                                                }),
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "h5",
                                                    className: "text-strong sm:typo-h4 w-full break-words",
                                                    children: currentTitle
                                                }),
                                                banners.length > 0 && /*#__PURE__*/ _jsx(CategoryBanner, {
                                                    banners: banners,
                                                    pageType: "system"
                                                })
                                            ]
                                        }),
                                        isSubjectPage && lnbItems.length > 0 && /*#__PURE__*/ _jsx(CategoryMenuGrid, {
                                            title: "전체 리스트",
                                            items: lnbItems,
                                            selectedCategoryId: selectedCategoryId,
                                            onCategorySelect: handleCategorySelect,
                                            getCategoryHref: getCategoryHref,
                                            expandable: true,
                                            defaultExpanded: false
                                        }),
                                        !isSubjectPage && !isLastDepth && directChildCategories.length > 0 && /*#__PURE__*/ _jsx(CategoryMenuGrid, {
                                            title: "더 자세히 찾기",
                                            items: directChildCategories,
                                            onCategorySelect: handleCategorySelect,
                                            getCategoryHref: getCategoryHref,
                                            variant: "titleOnly"
                                        }),
                                        isLastDepth && productsQuery.filterItems.length > 0 && /*#__PURE__*/ _jsx(ProductFilter, {
                                            filterItems: productsQuery.filterItems,
                                            selectedFilters: selectedFilters,
                                            onFilterChange: handleFilterChange,
                                            onReset: handleFilterReset,
                                            onMobileFilterOpen: openMobileFilter,
                                            isLoading: productsQuery.isFilterLoading
                                        }),
                                        /*#__PURE__*/ _jsx(ProductList, {
                                            items: productsQuery.products,
                                            totalCount: productsQuery.totalCount,
                                            sortOptions: productsQuery.sortOptions,
                                            selectedSort: selectedSort,
                                            onSortChange: handleSortChange,
                                            isLoading: productsQuery.isLoading,
                                            pagination: {
                                                currentPage,
                                                totalPages: Math.ceil(productsQuery.totalCount / ITEMS_PER_PAGE),
                                                onPageChange: handlePageChange
                                            },
                                            pageType: "system"
                                        })
                                    ]
                                }),
                                isLastDepth && productsQuery.filterItems.length > 0 && /*#__PURE__*/ _jsx(MobileFilterSheet, {
                                    isOpen: isMobileFilterOpen,
                                    onClose: closeMobileFilter,
                                    filterItems: productsQuery.filterItems,
                                    selectedFilters: selectedFilters,
                                    onFilterChange: handleFilterChange,
                                    initialExpandedKey: mobileFilterExpandedKey
                                }),
                                /*#__PURE__*/ _jsx(MobileCategoryDrawer, {
                                    isOpen: isMobileCategoryDrawerOpen,
                                    onClose: closeMobileCategoryDrawer,
                                    title: lnbTitle,
                                    items: lnbItems,
                                    selectedCategoryId: selectedCategoryId,
                                    onCategorySelect: handleCategorySelect
                                })
                            ]
                        })
                    ]
                })
            })
        })
    });
};
export default CategoryPage;
